import {
  BatteryChargingIcon,
  CalendarRangeIcon,
  DropletsIcon,
  FlameIcon,
  LeafIcon,
  SlidersHorizontalIcon,
  SproutIcon,
  SunIcon,
  WindIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de Energías Renovables y Transición Energética',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con las energías renovables puede ser el más transformador o el más aburrido. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Qué es la energía y por qué importan las fuentes renovables',
      'Diferencia entre energías renovables y no renovables',
      'Matrices energéticas y contexto latinoamericano',
      'Sostenibilidad y cambio climático',
      'Eficiencia energética y transición energética',
      'Material explicado de forma clara con ejemplos locales',
    ],
    result:
      'Tus alumnos entienden las energías renovables como una solución práctica y urgente, no como un conjunto de términos imposibles de memorizar.',
    icon: LeafIcon,
  },
  {
    number: '2',
    title: 'Energía Solar Fotovoltaica y Sistemas Solares',
    subtitle: 'Todo lo que necesitan saber sobre la tecnología más accesible',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan cómo funcionan realmente los paneles solares y los sistemas completos.',
    items: [
      'Principios de radiación solar y efecto fotovoltaico',
      'Paneles solares: tipos, características y eficiencia',
      'Inversores, reguladores y controladores',
      'Baterías solares y sistemas de almacenamiento',
      'Conexiones y protecciones eléctricas',
      'Sistemas aislados (off-grid) y conectados a red (on-grid)',
      'Cálculo y dimensionamiento básico de instalaciones fotovoltaicas',
      '15 ejemplos de cálculo resueltos paso a paso',
    ],
    result: 'Tus alumnos aprenden a dimensionar una instalación solar con método y confianza.',
    icon: SunIcon,
  },
  {
    number: '3',
    title: 'Energía Eólica y Sistemas de Generación',
    subtitle: 'Cómo enseñar energía eólica de forma clara y práctica',
    classroom:
      'La energía eólica es fascinante pero compleja. Este tomo te da todo lo que necesitas para que tus alumnos comprendan cómo el viento se convierte en electricidad.',
    items: [
      'Funcionamiento de la energía eólica',
      'Características del viento y velocidad promedio',
      'Aerogeneradores: componentes y funcionamiento',
      'Curvas de potencia y eficiencia',
      'Parques eólicos y sistemas de pequeña escala',
      'Consideraciones de instalación y mantenimiento',
      '8 casos prácticos simulados listos para el aula',
      'Ejercicios sobre cálculo de potencia',
    ],
    result: 'Tus alumnos comprenden por qué el viento es una fuente de energía tan valiosa en Latinoamérica.',
    icon: WindIcon,
  },
  {
    number: '4',
    title: 'Energía Hidráulica y Aprovechamientos Hidroenergéticos',
    subtitle: 'La energía del agua explicada de forma integral',
    classroom: 'La hidroelectricidad es compleja pero fundamental. Este tomo te enseña a desglosarla sin perder claridad.',
    items: [
      'Principios de generación hidroeléctrica',
      'Caudal, salto hidráulico y potencia',
      'Turbinas hidráulicas y tipos de centrales',
      'Centrales hidroeléctricas de gran escala',
      'Sistemas de pequeña y microescala',
      'Ventajas, limitaciones e impactos ambientales',
      'Análisis de casos en Latinoamérica',
      '10 casos de aplicación práctica',
    ],
    result:
      'Tus alumnos entienden por qué la energía hidráulica es tan importante en la región y cuáles son sus desafíos reales.',
    icon: DropletsIcon,
  },
  {
    number: '5',
    title: 'Biomasa, Biogás y Biocombustibles',
    subtitle: 'Aprovechamiento energético de residuos y recursos locales',
    classroom:
      'Biomasa y biogás suelen quedar fuera de los cursos. Este tomo te da las herramientas para incluirlas con confianza.',
    items: [
      'Aprovechamiento energético de residuos agrícolas, forestales, ganaderos y urbanos',
      'Biomasa sólida: características y aplicaciones',
      'Biodigestores: funcionamiento y diseño',
      'Producción de biogás y fermentación anaeróbica',
      'Biocombustibles: etanol y biodiesel',
      'Procesos de conversión energética',
      'Aplicaciones prácticas en contextos rurales',
      '6 proyectos simulados sobre biodigestores',
    ],
    result: 'Tus alumnos ven oportunidades de energía renovable en sus propios territorios rurales.',
    icon: SproutIcon,
  },
  {
    number: '6',
    title: 'Energía Geotérmica y Otras Fuentes Renovables',
    subtitle: 'Tecnologías complementarias y emergentes',
    classroom:
      'Geotermia, energía marina y otros sistemas complementarios. Este tomo cierra la visión integral de las renovables.',
    items: [
      'Fundamentos de geotermia',
      'Aplicaciones de la energía geotérmica en Latinoamérica',
      'Sistemas de calefacción y refrigeración geotérmica',
      'Energía marina: mareas y olas',
      'Aprovechamientos térmicos pasivos',
      'Sistemas emergentes y en desarrollo',
      'Análisis comparativo entre tecnologías',
      '5 casos aplicados a contextos específicos',
    ],
    result: 'Tus alumnos tienen una visión completa del espectro de energías renovables disponibles.',
    icon: FlameIcon,
  },
  {
    number: '7',
    title: 'Almacenamiento de Energía, Baterías y Sistemas Híbridos',
    subtitle: 'El corazón de la integración de renovables variables',
    classroom:
      'El almacenamiento es crítico. Este tomo te enseña a desglosar baterías, BESS y sistemas híbridos de forma clara.',
    items: [
      'Tipos de baterías: tecnologías y características',
      'Capacidad, potencia y ciclos de carga-descarga',
      'Autonomía y criterios de selección',
      'Sistemas BESS (Battery Energy Storage Systems)',
      'Sistemas híbridos: solar + eólico + almacenamiento',
      'Microredes y gestión inteligente de energía',
      'Combinación solar-eólica-batería',
      'Cálculo de autonomía',
      '12 casos prácticos de sistemas híbridos',
    ],
    result:
      'Tus alumnos entienden por qué el almacenamiento es la clave para que las renovables funcionen en la realidad.',
    icon: BatteryChargingIcon,
  },
  {
    number: '8',
    title: 'Diseño y Dimensionamiento de Sistemas de Energías Renovables',
    subtitle: 'El tomo más aplicado técnicamente',
    classroom: 'Este es el corazón práctico. Aquí tus alumnos aprenden a diseñar sistemas reales desde cero.',
    items: [
      'Cálculo de consumos y demanda energética',
      'Interpretación de datos climáticos y solares',
      'Estimación de generación solar y eólica',
      'Selección de componentes según necesidades',
      'Dimensionamiento de paneles, inversores y baterías',
      'Cálculo de pérdidas y rendimiento',
      'Autonomía y criterios de selección',
      'Comparación de alternativas técnicas',
      '20 ejercicios resueltos paso a paso',
      '8 casos prácticos completos de dimensionamiento',
    ],
    result: 'Tus alumnos salen preparados para dimensionar una instalación real.',
    icon: SlidersHorizontalIcon,
  },
  {
    number: '9',
    title: 'Enseñanza de las Energías Renovables — Planificación y Proyectos 2026',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Enfoques actuales para enseñar energías renovables',
      'Organización progresiva de contenidos',
      'Planificación anual orientativa',
      'Secuencias didácticas completas',
      'Prácticas de aula y laboratorio estructuradas',
      'Aprendizaje basado en proyectos',
      'Integración de herramientas digitales y simuladores',
      'Trabajo con recursos limitados',
      'Evaluación teórica y práctica',
      'Rúbricas y criterios de observación',
      '10 proyectos integradores listos para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: CalendarRangeIcon,
  },
];
